import { useEffect } from 'react';
import { useAuth } from '../auth/auth-provider';
import { applyAccentColor } from '../lib/accent-color';
import { useTenantSettings } from './use-tenant-settings';

/**
 * Keep the document painted with the workspace's accent colour.
 *
 * The setting was stored and read back into the settings screen but nothing
 * ever consumed it, so picking a colour changed only the swatch's own ring —
 * see `applyAccentColor` for what it drives.
 *
 * Mount once, inside the authenticated shell: the setting is behind auth, and
 * the fallback tokens in `globals.css` take over on the public pages. Branding
 * is read from the settings endpoint, which only `settings:write` may read, so
 * the query is skipped rather than attempted for a role that cannot have it —
 * those users keep the compiled-in accent.
 */
export function useBrandingTheme(): void {
  const { can } = useAuth();
  const { branding } = useTenantSettings({ enabled: can('settings:write') });
  const accentColor = branding.accentColor;

  useEffect(() => {
    applyAccentColor(accentColor);
    // Signing out unmounts the shell; the next workspace must not inherit
    // this one's colour.
    return () => applyAccentColor(undefined);
  }, [accentColor]);
}
